// Business-details tab of the advertiser Settings surface.
//
// Extracted verbatim from the "Business details" section of the former
// MyTeamPage. Read view shows name + website; Edit toggle swaps in inputs
// with a Save/Cancel row. Save PATCHes /advertisers/:id and refreshes the
// context memberships so every surface picks up the new advertiser name.

import React from 'react';
import { useParams } from 'react-router-dom';
import { Building2, Edit3, Loader2 } from 'lucide-react';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { usePublisherConfig } from '../../data/publisherConfigs';
import { apiClient, ApiError } from '../../lib/apiClient';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import PrimaryButton from '../../components/self-service/shared/PrimaryButton';
import Banner from '../../components/self-service/shared/Banner';

const SettingsBusinessTab: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { config } = usePublisherConfig(slug);
  const {
    selectedAdvertiser,
    advertiserMemberships,
    refreshMemberships,
  } = useAuth();

  const accent = config?.accentColor || config?.primaryColor;
  const primary = config?.primaryColor;

  const [editing, setEditing] = React.useState(false);
  const [editName, setEditName] = React.useState('');
  const [editWebsite, setEditWebsite] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setEditName(selectedAdvertiser?.advertiserName ?? '');
    setEditWebsite(selectedAdvertiser?.businessWebsite ?? '');
  }, [
    selectedAdvertiser?.advertiserId,
    selectedAdvertiser?.advertiserName,
    selectedAdvertiser?.businessWebsite,
  ]);

  if (advertiserMemberships !== null && advertiserMemberships.length === 0) {
    return <NoAdvertiserEmpty />;
  }

  const handleSave = async () => {
    if (!selectedAdvertiser) return;
    const name = editName.trim();
    if (!name) {
      setError('Business name is required.');
      return;
    }
    const confirmed = window.confirm(
      'Update this business? Changes affect every member of the team.',
    );
    if (!confirmed) return;
    setSaving(true);
    setError(null);
    try {
      await apiClient.patch(`/advertisers/${selectedAdvertiser.advertiserId}`, {
        name,
        businessWebsite: editWebsite.trim() || null,
      });
      analytics.track(EVENTS.ADVERTISER_PROFILE_UPDATED, {
        station_slug: slug,
        advertiser_id: selectedAdvertiser.advertiserId,
      });
      await refreshMemberships();
      setEditing(false);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to update business.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-900">Business details</h2>
        </div>
        {!editing && selectedAdvertiser && (
          <button
            type="button"
            onClick={() => {
              setEditing(true);
              setError(null);
            }}
            className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-all"
          >
            <Edit3 className="w-3 h-3" />
            Edit
          </button>
        )}
      </div>
      <div className="px-5 py-4">
        {!editing ? (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs text-slate-500 uppercase tracking-wide">Name</dt>
              <dd className="text-slate-900">{selectedAdvertiser?.advertiserName ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500 uppercase tracking-wide">Website</dt>
              <dd className="text-slate-900">
                {selectedAdvertiser?.businessWebsite || '—'}
              </dd>
            </div>
          </dl>
        ) : (
          <div className="space-y-3">
            <label className="block">
              <span className="text-xs text-slate-500 uppercase tracking-wide">Business name</span>
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500 uppercase tracking-wide">Business website</span>
              <input
                value={editWebsite}
                onChange={(e) => setEditWebsite(e.target.value)}
                placeholder="https://example.com"
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              />
            </label>
            {error && <Banner tone="warn" label="Couldn't save">{error}</Banner>}
            <div className="flex items-center gap-2">
              <PrimaryButton
                type="button"
                onClick={handleSave}
                disabled={saving}
                accentColor={accent}
                primaryColor={primary}
              >
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save changes
              </PrimaryButton>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setError(null);
                  setEditName(selectedAdvertiser?.advertiserName ?? '');
                  setEditWebsite(selectedAdvertiser?.businessWebsite ?? '');
                }}
                disabled={saving}
                className="px-3.5 py-1.5 rounded-full text-[12px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// The Team tab shows the same empty state — this component is intentionally
// duplicated here rather than shared, so each tab is self-contained. Security
// doesn't need it: password change works without an advertiser seat.
const NoAdvertiserEmpty: React.FC = () => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 text-center">
    <Building2 className="w-10 h-10 text-slate-400 mx-auto mb-4" />
    <h2 className="text-lg font-semibold text-slate-900 mb-2">No business yet</h2>
    <p className="text-sm text-slate-600">
      Submit a campaign first to set up an advertiser, then come back to manage the business.
    </p>
  </div>
);

export default SettingsBusinessTab;
